import React, { useState } from 'react';
import { Box } from '@mui/material';
import { PillTabs } from '@ui/PillTabs';
import { LIFECYCLE_GROUPS } from '@features/lifecycles/lifecycle-groups.data';
import {
  useEnableLifecycleGroup,
  useSelectedLifecycles,
  useSelectLifecycle,
  useToggleLifecycleGroup,
} from '@features/lifecycles/lifecycle-scope.hooks';
import { useBuildingLifetime } from '@client/store/project/project.hook';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { LifecycleGroupTooltip } from './LifecycleGroupTooltip';
import { LifetimeEditor } from './LifetimeEditor';
import { PhaseToggle } from './PhaseToggle';

const LIFECYCLE_TABS = ['embodied', 'operation', 'endOfLife'] as const;

type LifecycleControlsProps = {
  previewLifecycles?: readonly Lifecycle[];
  previewLifetime?: number;
  allowLifetimeEditing?: boolean;
};

/** Lifecycle group and phase controls shared by the build scope overlay. */
export const LifecycleControls: React.FC<LifecycleControlsProps> = ({
  previewLifecycles,
  previewLifetime,
  allowLifetimeEditing = true,
}) => {
  const storedLifecycles = useSelectedLifecycles();
  const selectLifecycle = useSelectLifecycle();
  const toggleLifecycleGroup = useToggleLifecycleGroup();
  const enableLifecycleGroup = useEnableLifecycleGroup();
  const storedLifetime = useBuildingLifetime();
  const selectedLifecycles = previewLifecycles ?? storedLifecycles;
  const lifetime = previewLifetime ?? storedLifetime;
  const [lifetimeHovered, setLifetimeHovered] = useState(false);
  const activeGroups = LIFECYCLE_GROUPS.filter((group) =>
    group.toggleableLifecycles.some((phase) =>
      selectedLifecycles.includes(phase),
    ),
  );
  const operationIsActive = activeGroups.some(({ id }) => id === 'operation');

  return (
    <PillTabs
      fontSize={13}
      fontWeight={600}
      groupVariant="fill"
      groupClickable
      tabs={LIFECYCLE_TABS}
      value={activeGroups.map(({ id }) => id)}
      onDoubleClick={(id) => {
        const group = LIFECYCLE_GROUPS.find((candidate) => candidate.id === id);
        if (group) enableLifecycleGroup(group.toggleableLifecycles);
      }}
      onChange={(id) => {
        const group = LIFECYCLE_GROUPS.find((candidate) => candidate.id === id);
        if (!group) return;
        if (activeGroups.length === 1 && activeGroups[0]?.id === id) return;
        toggleLifecycleGroup(group.id, group.toggleableLifecycles);
      }}
      labelComponent="span"
      wrapTab={(tab, button) => {
        const group = LIFECYCLE_GROUPS.find(({ id }) => id === tab);
        if (!group) return button;
        return (
          <LifecycleGroupTooltip
            group={group}
            active={activeGroups.includes(group)}
            lifetimeHovered={lifetimeHovered}
            placement="bottom"
          >
            {button}
          </LifecycleGroupTooltip>
        );
      }}
      labels={{
        operation:
          operationIsActive && allowLifetimeEditing ? (
            <>
              {'Operation '}
              <LifetimeEditor
                lifetime={lifetime}
                active
                onHoverChange={setLifetimeHovered}
                placement="bottom"
              />
              {' years'}
            </>
          ) : (
            'Operation'
          ),
        endOfLife: 'End-of-life',
      }}
      groupContent={(tab) => {
        const group = LIFECYCLE_GROUPS.find(({ id }) => id === tab);
        if (!group) return null;
        const groupIsActive = activeGroups.includes(group);
        return (
          <Box
            sx={{
              display: 'flex',
              maxWidth: groupIsActive ? '320px' : 0,
              overflow: 'hidden',
              opacity: groupIsActive ? 1 : 0,
              visibility: groupIsActive ? 'visible' : 'hidden',
              transition: groupIsActive
                ? 'max-width 220ms ease, opacity 160ms ease 60ms'
                : 'max-width 220ms ease, opacity 120ms ease, visibility 0s linear 220ms',
            }}
          >
            <Box
              role="group"
              aria-label={group.label}
              aria-hidden={!groupIsActive}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                ml: '4px',
                pr: '4px',
                flexShrink: 0,
              }}
            >
              {group.phases.map((phase) => (
                <PhaseToggle
                  key={phase.label}
                  {...phase}
                  groupActive={groupIsActive}
                  tooltipActive={groupIsActive}
                  selected={
                    phase.lifecycle !== null &&
                    selectedLifecycles.includes(phase.lifecycle)
                  }
                  onSelectPhase={selectLifecycle}
                />
              ))}
            </Box>
          </Box>
        );
      }}
    />
  );
};
